import {
  Button,
  ModalIcon,
  ModalProps,
  Stack,
  TextBody,
  TextTitle,
} from '@pypestream/design-system';
import { TranslationComponent } from '@pypestream/translations';
import { FC, useRef } from 'react';

import { GenericModal } from '../generic-modal/generic-modal';

export interface UnsavedChangesModalViewProps {
  open: boolean;
  isSaving: boolean;
  hasSaveError: boolean;
  message?: string;
  /** Any dismiss (X, Escape, programmatic close) — the user stays on the page. */
  onStay: () => void;
  onLeave: () => void;
  onSaveAndLeave: () => void;
  /** Machine-level botFlowError backstop (see GenericModal.onError). */
  onError: () => void;
}

/** Presentational only — the navigation-guard mechanics live in `useUnsavedChanges`. */
export const UnsavedChangesModalView: FC<UnsavedChangesModalViewProps> = ({
  open,
  isSaving,
  hasSaveError,
  message,
  onStay,
  onLeave,
  onSaveAndLeave,
  onError,
}) => {
  // Unused — `open` drives the modal declaratively. Only here because
  // GenericModal requires modalRef.
  const modalRef = useRef<ModalProps>(null);

  // usePortal={false} because both actions navigate while the modal is still
  // open, unmounting the host page. Under the default portal <ps-modal> moves
  // itself into document.body, so React can no longer remove it on unmount and
  // it orphans on screen stuck at "Saving…".
  return (
    <GenericModal
      test-id="unsaved-changes-modal"
      modalRef={modalRef}
      open={open}
      onClose={onStay}
      onError={onError}
      stayOnClickOutside
      usePortal={false}
    >
      <ModalIcon
        name="error"
        slot="header"
        style={{ display: 'flex', justifyContent: 'center' }}
      />
      <Stack slot="header" alignItems="center" direction="column">
        <TextTitle size="small">
          <TranslationComponent i18nKey="pro-studio/common:unsavedChangesModal.title">
            You have unsaved changes
          </TranslationComponent>
        </TextTitle>
        {message ? (
          <TextBody variant="secondary">{message}</TextBody>
        ) : (
          <TextBody variant="secondary">
            <TranslationComponent i18nKey="pro-studio/common:unsavedChangesModal.body">
              If you leave now, your changes will be lost.
            </TranslationComponent>
          </TextBody>
        )}
        {hasSaveError ? (
          <TextBody variant="secondary" test-id="unsaved-changes-modal-error">
            <TranslationComponent i18nKey="pro-studio/common:unsavedChangesModal.saveError">
              Your changes could not be saved. Please try again.
            </TranslationComponent>
          </TextBody>
        ) : null}
      </Stack>
      <Stack slot="footer" justifyContent="center">
        <Button
          test-id="unsaved-changes-modal-leave"
          variant="warning"
          size="large"
          onClick={onLeave}
          disabled={isSaving}
        >
          <TranslationComponent i18nKey="pro-studio/common:unsavedChangesModal.leave">
            Leave without saving
          </TranslationComponent>
        </Button>
        <Button
          test-id="unsaved-changes-modal-save"
          variant="primary"
          size="large"
          onClick={onSaveAndLeave}
          disabled={isSaving}
        >
          {isSaving ? (
            <TranslationComponent i18nKey="pro-studio/common:unsavedChangesModal.saving">
              Saving...
            </TranslationComponent>
          ) : (
            <TranslationComponent i18nKey="pro-studio/common:unsavedChangesModal.saveAndLeave">
              Save and leave
            </TranslationComponent>
          )}
        </Button>
      </Stack>
    </GenericModal>
  );
};
